import React, { FC, useCallback, useMemo } from 'react';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { ItemWidth, ResponsiveCarousel } from 'components/responsive-carousel';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { useMParticleContext } from 'state/mParticle';
import { useStoreContext } from 'state/store';
import { isCartEntryInMenu } from 'utils/cart';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { logAddRecentItemToCartEvent } from 'utils/mParticle';

import RecentItemsCarouselItem from './recent-items-carousel-item';
import { Container } from './styled';
import { IRecentItemsProps } from './types';

const sanityIdKeyExtractor = ({ sanityId }: IBackendCartEntries) => sanityId;

const RecentItemsCarousel: FC<React.PropsWithChildren<IRecentItemsProps>> = ({
  locationName,
  containerHasTopPadding = true,
}) => {
  const { user } = useAuthContext();
  const mParticle = useMParticleContext();
  const { store } = useStoreContext();

  const uniquePurchasedItems = user?.uniquePurchasedItems;
  const items = useMemo(
    () => (uniquePurchasedItems || []).filter(item => !item.isDonation && isCartEntryInMenu(item)),
    [uniquePurchasedItems]
  );

  const addAndLogItemToCart = useCallback(
    (item: IBackendCartEntries) => {
      const { name } = item;
      if (name) {
        logAddRecentItemToCartEvent(name ?? '', store ?? {}, mParticle.logRBIEvent);
      }
      hapticImpact({ style: HapticsImpactStyle.Heavy });
    },
    [items.length, locationName, mParticle, store]
  );

  const carouselItemRenderer = useCallback(
    ({ item }: { item: IBackendCartEntries }) => (
      <RecentItemsCarouselItem cartEntry={item} onItemAdded={() => addAndLogItemToCart(item)} />
    ),
    [addAndLogItemToCart]
  );

  const isMobile = useIsMobileBreakpoint();
  const itemWidth = isMobile
    ? ItemWidth.VERTICAL_CAROUSEL_ITEM_MOBILE
    : ItemWidth.VERTICAL_CAROUSEL_ITEM_WEB;

  return (
    <Container $containerHasTopPadding={containerHasTopPadding}>
      <ResponsiveCarousel
        itemWidth={itemWidth}
        bgColor={'transparent'}
        data={items}
        renderItem={carouselItemRenderer}
        keyExtractor={sanityIdKeyExtractor}
        loop={false}
        inactiveSlideOpacity={1}
        inactiveSlideScale={0.9}
      />
    </Container>
  );
};

export default RecentItemsCarousel;
